import { PROJECT_TYPES } from "@/lib/intake-schema";

export type Discipline = {
  slug: string;
  projectType: (typeof PROJECT_TYPES)[number];
  label: string;
  headline: string;
  subheadline: string;
  sampleScope: string;
  sampleDeliverables: string;
  faqs: { q: string; a: string }[];
  features: { emoji: string; title: string; text: string }[];
  whyParagraph: string;
  relatedSlugs: string[];
};

export const DISCIPLINES: Discipline[] = [
  {
    slug: "web-design",
    projectType: "Web Design",
    label: "Web Designers",
    headline: "Free Proposal & Contract Generator for Web Designers",
    subheadline:
      "Stop rewriting the same scope-of-work every time a new client wants a site. DealDraft writes your proposal and contract together, tailored to web design projects — in 60 seconds.",
    sampleScope:
      "You're redesigning a 6-page marketing site for a local dental practice, including a new visual identity, mobile-responsive layout, and a lightweight CMS so their staff can update copy without a developer.",
    sampleDeliverables:
      "Custom-designed homepage + 5 interior pages, responsive across breakpoints, CMS setup and training, 2 rounds of revisions.",
    faqs: [
      {
        q: "Does the contract cover website ownership and source files?",
        a: "Yes. The generated contract includes an IP/ownership clause covering final deliverables, source files, and what happens to draft or unused concepts — standard for web design engagements.",
      },
      {
        q: "Can I include hosting or maintenance as a separate line item?",
        a: "Yes — describe it in the deliverables field, e.g. 12 months of hosting and monthly backups, and it'll be written into both the proposal and the contract's scope section.",
      },
      {
        q: "What if the client wants ongoing updates after launch?",
        a: "Note it in your scope description and DealDraft will include a maintenance or retainer clause; for ongoing work we recommend the Monthly retainer payment option in the generator.",
      },
      {
        q: "Does the contract structure payment around project milestones?",
        a: "Yes — choose 3 equal installments or describe a custom milestone split (e.g. 50% deposit, 25% at design approval, 25% at launch) in your scope, and it's reflected in both documents.",
      },
      {
        q: "What happens to unused design concepts if the client picks a different direction?",
        a: "The IP clause addresses this: unused concepts and drafts remain yours unless you explicitly transfer them, which keeps your exploratory work reusable for future clients.",
      },
    ],
    features: [
      {
        emoji: "🧠",
        title: "AI-Written Proposals",
        text: "Professional copy that speaks to redesigns, rebuilds, and new-build sites — not generic freelance boilerplate.",
      },
      {
        emoji: "📄",
        title: "Legal Contract Included",
        text: "11-clause agreement with a full IP transfer clause, so ownership of the final site and source files passes to your client on payment.",
      },
      {
        emoji: "🎯",
        title: "Industry-Specific",
        text: "Built around milestone-based web projects — deposit, build, launch — not one-off gigs.",
      },
      {
        emoji: "⚡",
        title: "60-Second Generation",
        text: "From client call to a scoped proposal faster than you can open a blank doc.",
      },
      {
        emoji: "📥",
        title: "PDF Export",
        text: "Download both documents, print, or email straight to your client.",
      },
      {
        emoji: "🔒",
        title: "Your Documents, Saved",
        text: "Every proposal and contract you've sent, organized in one dashboard.",
      },
    ],
    whyParagraph:
      "Web design proposals live or die on scope clarity — pages, revisions, CMS training, and what happens after launch. DealDraft writes a milestone-based payment structure and a full IP transfer clause into every contract, so there's no ambiguity about who owns the final site once you're paid.",
    relatedSlugs: ["software-development", "graphic-design", "consulting"],
  },
  {
    slug: "graphic-design",
    projectType: "Graphic Design",
    label: "Graphic Designers",
    headline: "Free Proposal & Contract Generator for Graphic Designers",
    subheadline:
      "Logo packages, brand identities, print collateral — DealDraft turns your project details into a polished proposal and a contract that protects your revisions and usage rights.",
    sampleScope:
      "You're designing a full brand identity for a new coffee roastery: logo suite, color palette, packaging labels for 4 product lines, and a one-page brand guide.",
    sampleDeliverables:
      "Primary + secondary logo, color and type system, 4 packaging labels, one-page brand style guide, source files in AI/PNG/SVG.",
    faqs: [
      {
        q: "Does the contract limit how many revision rounds I owe?",
        a: "Yes — describe your revision policy in the scope, e.g. 2 rounds of revisions included, and the contract will formalize it, protecting you from endless rework requests.",
      },
      {
        q: "Can it address usage rights and licensing?",
        a: "Yes — it depends on the project. Brand identity, logo, and packaging work usually transfers full ownership to the client on final payment, the same model as web design. Illustration or stock-style work often stays licensed rather than sold outright — you keep the rights and grant usage, the same model as photography. Specify which applies in your scope and the contract reflects it.",
      },
      {
        q: "Is it good for one-off logo projects or only full branding?",
        a: "Both — pick Graphic Design as the project type and describe the scope; the generator adapts the language whether it's a single logo or a full identity system.",
      },
      {
        q: "Can the contract distinguish between a deposit and milestone payments?",
        a: "Yes — many designers use a deposit plus final-payment structure for smaller projects (logos) and a milestone split for larger brand identity work; describe your structure in the scope and it's written in.",
      },
      {
        q: "Does it clarify whether I can show the work in my portfolio?",
        a: "Note your portfolio-rights expectation in the scope description (e.g. non-exclusive right to display in portfolio and case studies) and DealDraft includes it alongside the ownership/licensing clause.",
      },
    ],
    features: [
      {
        emoji: "🧠",
        title: "AI-Written Proposals",
        text: "Copy built for logo suites, brand identities, and packaging — not just 'design services.'",
      },
      {
        emoji: "📄",
        title: "Legal Contract Included",
        text: "11-clause agreement with an ownership clause that adapts to the work — full transfer for brand identity, licensed usage for illustration — so rights are explicit before you deliver files.",
      },
      {
        emoji: "🎯",
        title: "Industry-Specific",
        text: "Tailored for graphic design's two ownership models — full transfer for brand work, licensing for illustration.",
      },
      {
        emoji: "⚡",
        title: "60-Second Generation",
        text: "From client call to a scoped proposal faster than sketching a first concept.",
      },
      {
        emoji: "📥",
        title: "PDF Export",
        text: "Download both documents, print, or email straight to your client.",
      },
      {
        emoji: "🔒",
        title: "Your Documents, Saved",
        text: "Every proposal and contract you've sent, organized in one dashboard.",
      },
    ],
    whyParagraph:
      "Graphic design splits into two ownership models. Brand identity, logo, and packaging work usually transfers full ownership on final payment — the same model as web design. Illustration and stock-style work more often stays licensed, with you retaining rights and granting usage — the same model as photography. DealDraft's contract lets you pick whichever applies, instead of one-size-fits-all ownership language.",
    relatedSlugs: ["web-design", "copywriting", "social-media-management"],
  },
  {
    slug: "copywriting",
    projectType: "Copywriting & Content",
    label: "Copywriters",
    headline: "Free Proposal & Contract Generator for Copywriters",
    subheadline:
      "Website copy, blog retainers, email sequences — get a client-ready proposal and a contract covering revisions, usage rights, and deadlines in under a minute.",
    sampleScope:
      "You're writing a 12-post blog content calendar for a B2B SaaS company, targeting their onboarding and feature-adoption funnel over the next quarter.",
    sampleDeliverables:
      "12 SEO-optimized blog posts (800–1,000 words each), keyword briefs, one round of client revisions per post.",
    faqs: [
      {
        q: "Does it work for ongoing content retainers, not just one-off projects?",
        a: "Yes — choose the Monthly retainer payment terms option and describe your recurring deliverables, e.g. 4 blog posts per month; the contract reflects a retainer structure.",
      },
      {
        q: "Does the contract cover who owns the published copy?",
        a: "Yes, it includes an IP clause specifying that ownership transfers to the client upon full payment — standard for copywriting work.",
      },
      {
        q: "Can I specify revision limits so scope doesn't creep?",
        a: "Yes — state your revision policy in the scope field and it becomes part of both the proposal and the contract, so it's agreed upfront.",
      },
      {
        q: "Is it different for a single project versus a monthly retainer?",
        a: "Yes — for one-off projects, choose a per-project payment split; for ongoing work, select Monthly retainer and describe your recurring deliverables (e.g. 4 blog posts per month) so the contract reflects a retainer relationship, not a fixed-scope project.",
      },
      {
        q: "Does it cover who writes the brief versus who approves it?",
        a: "Describe your intake and approval process in the scope (e.g. client provides key messaging, one round of approval before publishing) and it's incorporated into the proposal's process section.",
      },
    ],
    features: [
      {
        emoji: "🧠",
        title: "AI-Written Proposals",
        text: "Copy that reflects blog retainers, launch copy, and email sequences — not vague 'writing services.'",
      },
      {
        emoji: "📄",
        title: "Legal Contract Included",
        text: "11-clause agreement that transfers ownership of the finished copy to your client on full payment, with revision limits built in.",
      },
      {
        emoji: "🎯",
        title: "Industry-Specific",
        text: "Tailored for both one-off projects and ongoing content retainers.",
      },
      {
        emoji: "⚡",
        title: "60-Second Generation",
        text: "From client call to a scoped proposal faster than drafting a single headline.",
      },
      {
        emoji: "📥",
        title: "PDF Export",
        text: "Download both documents, print, or email straight to your client.",
      },
      {
        emoji: "🔒",
        title: "Your Documents, Saved",
        text: "Every proposal and contract you've sent, organized in one dashboard.",
      },
    ],
    whyParagraph:
      "Copywriting spans one-off launch projects and open-ended monthly retainers, and the payment structure should match. DealDraft writes per-project deliverable terms or a recurring retainer structure depending on what you select, with an ownership clause that transfers the finished copy to your client once you're paid in full.",
    relatedSlugs: ["graphic-design", "social-media-management", "consulting"],
  },
  {
    slug: "photography",
    projectType: "Photography",
    label: "Photographers",
    headline: "Free Proposal & Contract Generator for Photographers",
    subheadline:
      "Weddings, product shoots, headshots — DealDraft writes a proposal and a shoot contract covering usage rights, delivery timelines, and payment in 60 seconds.",
    sampleScope:
      "You're shooting a half-day product photography session for an e-commerce skincare brand: 25 hero product shots and 15 lifestyle shots for their site relaunch.",
    sampleDeliverables:
      "40 final edited images delivered as a high-res JPEG + web-optimized set, online gallery for selects, usage rights for web and social.",
    faqs: [
      {
        q: "Does the contract cover image usage and licensing?",
        a: "Yes — it includes a rights and usage clause; specify whether you're granting full buyout, web-only use, or a licensing term in your scope so it's captured accurately.",
      },
      {
        q: "Can it include a retainer or booking deposit?",
        a: "Yes, choose the 50% upfront payment terms and describe it as a booking retainer in your scope — this is written into the proposal and contract as a deposit to secure the date.",
      },
      {
        q: "Is it suited for weddings as well as commercial shoots?",
        a: "Yes — select Photography as the project type for either; describe the shoot type and deliverables and the AI tailors the language accordingly.",
      },
      {
        q: "Does payment happen before or after the shoot?",
        a: "Most photographers take a deposit to book the date, then invoice the balance Net 30 after delivery; choose the 50% upfront option and describe the remainder as due on delivery in your scope.",
      },
      {
        q: "Can I limit how many images I deliver versus how many I shoot?",
        a: "Yes — specify the shoot count and the number of final edited images in your deliverables (e.g. 200 shot, 40 delivered) so client expectations match your actual workflow.",
      },
    ],
    features: [
      {
        emoji: "🧠",
        title: "AI-Written Proposals",
        text: "Copy built for shoots and sessions — weddings, product, headshots — not generic 'creative services.'",
      },
      {
        emoji: "📄",
        title: "Legal Contract Included",
        text: "11-clause agreement with a usage-rights clause: you retain broader rights while granting your client a license, not a full buyout by default.",
      },
      {
        emoji: "🎯",
        title: "Industry-Specific",
        text: "Tailored for session-based work — booking deposit, shoot day, delivery timeline.",
      },
      {
        emoji: "⚡",
        title: "60-Second Generation",
        text: "From booking call to a client-ready proposal faster than reviewing a single gallery.",
      },
      {
        emoji: "📥",
        title: "PDF Export",
        text: "Download both documents, print, or email straight to your client.",
      },
      {
        emoji: "🔒",
        title: "Your Documents, Saved",
        text: "Every proposal and contract you've sent, organized in one dashboard.",
      },
    ],
    whyParagraph:
      "Photography works on a session timeline, not a project-phase one — consult, shoot, edit, deliver — and payment usually follows the shoot rather than milestones during it. DealDraft defaults to a booking deposit plus Net 30 post-delivery payment, and a usage clause that lets you retain broader rights while licensing your client a personal or commercial use grant.",
    relatedSlugs: ["video-production", "social-media-management", "graphic-design"],
  },
  {
    slug: "video-production",
    projectType: "Video Production",
    label: "Videographers",
    headline: "Free Proposal & Contract Generator for Video Production",
    subheadline:
      "Commercials, brand films, social content — send a client-ready proposal and production contract covering revisions, rights, and delivery specs in 60 seconds.",
    sampleScope:
      "You're producing a 90-second brand video for a fitness studio's website and social channels, including on-location filming, interviews, and licensed music.",
    sampleDeliverables:
      "One 90-second hero video, three 15-second social cutdowns, raw footage archive, 2 rounds of revisions.",
    faqs: [
      {
        q: "Does the contract cover music and footage licensing?",
        a: "It includes an IP and rights clause you can tailor — note any licensed music, stock footage, or usage restrictions in your scope description.",
      },
      {
        q: "Can I set limits on revision rounds for editing?",
        a: "Yes — state your revision policy, e.g. 2 rounds included, additional rounds billed hourly, in the scope field and it's built into both documents.",
      },
      {
        q: "Does it work for ongoing content production, like a monthly video retainer?",
        a: "Yes — use the Monthly retainer payment option and describe your recurring deliverables; the contract reflects an ongoing production relationship.",
      },
      {
        q: "Does the contract cover raw footage delivery separately from the final edit?",
        a: "Yes — list raw footage as a distinct deliverable if you're including it (e.g. raw footage archive, final 90-second cut), since usage rights can differ between the two.",
      },
      {
        q: "How does payment typically work for a shoot-based project?",
        a: "A deposit to hold the shoot date plus a balance due on delivery (often Net 30) is common; choose the 50% upfront option and note the balance timing in your scope.",
      },
    ],
    features: [
      {
        emoji: "🧠",
        title: "AI-Written Proposals",
        text: "Copy built for shoots, edits, and deliverables — brand films, commercials, social cutdowns.",
      },
      {
        emoji: "📄",
        title: "Legal Contract Included",
        text: "11-clause agreement with a usage-rights clause: you can retain reel/portfolio rights while licensing the final cut to your client.",
      },
      {
        emoji: "🎯",
        title: "Industry-Specific",
        text: "Tailored for session-based production work — pre-production, shoot day, post, delivery.",
      },
      {
        emoji: "⚡",
        title: "60-Second Generation",
        text: "From client call to a scoped proposal faster than logging a single clip.",
      },
      {
        emoji: "📥",
        title: "PDF Export",
        text: "Download both documents, print, or email straight to your client.",
      },
      {
        emoji: "🔒",
        title: "Your Documents, Saved",
        text: "Every proposal and contract you've sent, organized in one dashboard.",
      },
    ],
    whyParagraph:
      "Like photography, video production runs on a shoot-and-deliver timeline rather than sequential project phases, and payment commonly follows delivery. DealDraft's default usage clause lets you retain the right to use footage in your reel or portfolio while granting your client a license for the platforms they need — web, social, broadcast — instead of an automatic full buyout.",
    relatedSlugs: ["photography", "social-media-management", "copywriting"],
  },
  {
    slug: "software-development",
    projectType: "Software Development",
    label: "Developers",
    headline: "Free Proposal & Contract Generator for Developers",
    subheadline:
      "Client apps, integrations, MVPs — DealDraft writes a scoped proposal and a development contract covering IP, milestones, and payment terms in 60 seconds.",
    sampleScope:
      "You're building a custom internal inventory-tracking web app for a mid-size retailer, including a database, admin dashboard, and CSV export.",
    sampleDeliverables:
      "Full-stack web application, admin dashboard, CSV export feature, deployment to the client's hosting, 30 days of post-launch bug fixes.",
    faqs: [
      {
        q: "Does the contract cover code ownership and IP transfer?",
        a: "Yes, it includes an IP clause covering ownership of the final codebase upon payment — note if you're retaining rights to reusable internal libraries or boilerplate.",
      },
      {
        q: "Can I structure payment around milestones instead of a flat split?",
        a: "Choose 3 equal installments for milestone-style billing, or describe your milestone structure in the scope field and it'll be reflected in the proposal.",
      },
      {
        q: "Does it cover post-launch support or bug-fix windows?",
        a: "Yes — describe your support window, e.g. 30 days of bug fixes included, in the deliverables field and it's written into the contract.",
      },
      {
        q: "Can I structure payment around specific project milestones instead of thirds?",
        a: "Yes — describe your milestone structure in the scope field (e.g. 30% to start, 40% at staging deploy, 30% at launch) and it's reflected in the proposal's payment section.",
      },
      {
        q: "Does it address what happens if scope changes mid-project?",
        a: "The generated contract includes a change-order style clause; describe your process for handling scope changes (e.g. additional features billed hourly) in the scope field so it's captured upfront.",
      },
    ],
    features: [
      {
        emoji: "🧠",
        title: "AI-Written Proposals",
        text: "Copy built for MVPs, integrations, and internal tools — not generic 'tech services.'",
      },
      {
        emoji: "📄",
        title: "Legal Contract Included",
        text: "11-clause agreement with a full IP transfer clause covering the final codebase, plus room to carve out reusable libraries you keep.",
      },
      {
        emoji: "🎯",
        title: "Industry-Specific",
        text: "Tailored for milestone-based dev projects — scope, build, deploy, support window.",
      },
      {
        emoji: "⚡",
        title: "60-Second Generation",
        text: "From client call to a scoped proposal faster than writing a single ticket.",
      },
      {
        emoji: "📥",
        title: "PDF Export",
        text: "Download both documents, print, or email straight to your client.",
      },
      {
        emoji: "🔒",
        title: "Your Documents, Saved",
        text: "Every proposal and contract you've sent, organized in one dashboard.",
      },
    ],
    whyParagraph:
      "Client dev work is phase-based — scope, build, deploy, then a support window — and payment should track those milestones rather than a single lump sum. DealDraft defaults to milestone installments and an IP clause that transfers the final codebase to your client on payment, while leaving room to note any reusable internal libraries or boilerplate you retain.",
    relatedSlugs: ["web-design", "consulting", "graphic-design"],
  },
  {
    slug: "consulting",
    projectType: "Consulting",
    label: "Consultants",
    headline: "Free Proposal & Contract Generator for Consultants",
    subheadline:
      "Strategy engagements, audits, fractional work — get a proposal and a service agreement covering scope, confidentiality, and payment terms in 60 seconds.",
    sampleScope:
      "You're running a 6-week operations audit for a 40-person logistics company, including stakeholder interviews, a process review, and a final recommendations report.",
    sampleDeliverables:
      "Stakeholder interview summary, current-state process map, final report with prioritized recommendations, one presentation to leadership.",
    faqs: [
      {
        q: "Does the contract include a confidentiality clause?",
        a: "Yes — the generated agreement includes standard confidentiality language appropriate for consulting engagements involving sensitive business information.",
      },
      {
        q: "Can it handle a retainer-based advisory arrangement instead of a project?",
        a: "Yes — select Monthly retainer as the payment terms and describe your ongoing advisory scope; the documents reflect an open-ended engagement rather than a fixed project.",
      },
      {
        q: "Does it cover what happens if the engagement ends early?",
        a: "The contract includes a cancellation and kill-fee clause covering early termination by either party — standard across all DealDraft contracts.",
      },
      {
        q: "Does the contract cover milestone payments across a multi-week engagement?",
        a: "Yes — describe your phase structure in the scope (e.g. discovery week, analysis phase, final report) and choose a milestone-style installment split so payment tracks the engagement's progress.",
      },
      {
        q: "Is it suited for fractional or ongoing advisory work, not just fixed audits?",
        a: "Yes — select Monthly retainer as the payment terms and describe your ongoing advisory scope; the documents reflect an open-ended engagement rather than a one-time project.",
      },
    ],
    features: [
      {
        emoji: "🧠",
        title: "AI-Written Proposals",
        text: "Copy built for audits, strategy engagements, and fractional work — not generic 'consulting services.'",
      },
      {
        emoji: "📄",
        title: "Legal Contract Included",
        text: "11-clause agreement with confidentiality language and a cancellation/kill-fee clause for early termination by either party.",
      },
      {
        emoji: "🎯",
        title: "Industry-Specific",
        text: "Tailored for phase-based advisory engagements — discovery, analysis, report, presentation.",
      },
      {
        emoji: "⚡",
        title: "60-Second Generation",
        text: "From discovery call to a scoped proposal faster than drafting a single slide.",
      },
      {
        emoji: "📥",
        title: "PDF Export",
        text: "Download both documents, print, or email straight to your client.",
      },
      {
        emoji: "🔒",
        title: "Your Documents, Saved",
        text: "Every proposal and contract you've sent, organized in one dashboard.",
      },
    ],
    whyParagraph:
      "Consulting engagements are phase-based like web design or dev work — discovery, analysis, a final report — and DealDraft defaults to milestone-style payment across those phases. Because you're often handling sensitive internal information rather than delivering IP-bearing files, the contract leads with confidentiality and a clear kill-fee for early termination instead of a file-ownership clause.",
    relatedSlugs: ["software-development", "web-design", "copywriting"],
  },
  {
    slug: "social-media-management",
    projectType: "Social Media Management",
    label: "Social Media Managers",
    headline: "Free Proposal & Contract Generator for Social Media Managers",
    subheadline:
      "Monthly content calendars, community management, paid campaigns — DealDraft writes a proposal and retainer contract tailored to social media work in 60 seconds.",
    sampleScope:
      "You're managing Instagram and TikTok for a boutique fitness brand: content calendar, daily posting, community engagement, and a monthly performance report.",
    sampleDeliverables:
      "20 posts/month across Instagram and TikTok, content calendar, daily community management, monthly analytics report.",
    faqs: [
      {
        q: "Is this built for monthly retainers rather than one-off projects?",
        a: "Yes — select Monthly retainer as the payment terms; the generated contract is structured around recurring monthly deliverables rather than a single fixed-scope project.",
      },
      {
        q: "Does the contract cover who owns login credentials and account access?",
        a: "Describe access and ownership expectations in your scope, e.g. client retains all account ownership and passwords, so the AI reflects it in the contract.",
      },
      {
        q: "Can it include ad spend or paid campaign management separately from organic content?",
        a: "Yes — list paid campaign management as a distinct deliverable in the deliverables field and it'll appear as a separate line item in both documents.",
      },
      {
        q: "How do revisions work for an ongoing content calendar instead of a single deliverable?",
        a: "Rather than a flat revision count for one deliverable, describe your review cycle per content batch in the scope, e.g. one round of approval per week's posts before they go live, and DealDraft's contract reflects revisions per posting cycle instead of a fixed 2-round limit.",
      },
      {
        q: "Does it cover what happens to the account and content calendar if the client cancels?",
        a: "Yes — social media retainers run on notice-based cancellation rather than a percentage kill fee, since there's no fixed remaining balance in an open-ended engagement. Describe your notice period in the scope, e.g. 30 days' written notice, plus handoff of login credentials and the content calendar within 5 business days after that period ends, and DealDraft's termination clause reflects it.",
      },
    ],
    features: [
      {
        emoji: "🧠",
        title: "AI-Written Proposals",
        text: "Copy built for monthly content calendars and community management — not one-off project language.",
      },
      {
        emoji: "📄",
        title: "Legal Contract Included",
        text: "11-clause agreement structured around recurring deliverables, with room to specify who owns login credentials and account access.",
      },
      {
        emoji: "🎯",
        title: "Industry-Specific",
        text: "Tailored for retainer-based work — content calendar, daily posting, monthly reporting, on repeat.",
      },
      {
        emoji: "⚡",
        title: "60-Second Generation",
        text: "From client call to a scoped proposal faster than drafting a single caption.",
      },
      {
        emoji: "📥",
        title: "PDF Export",
        text: "Download both documents, print, or email straight to your client.",
      },
      {
        emoji: "🔒",
        title: "Your Documents, Saved",
        text: "Every proposal and contract you've sent, organized in one dashboard.",
      },
    ],
    whyParagraph:
      "Unlike a fixed-scope project or a single shoot, social media work is ongoing — a content calendar and posting cadence that repeats every month. DealDraft defaults to a monthly retainer structure rather than milestone or per-session payment, and includes room to spell out account and login ownership, which matters more here than in almost any other discipline.",
    relatedSlugs: ["copywriting", "photography", "video-production"],
  },
];

export const DISCIPLINE_BY_SLUG = new Map(DISCIPLINES.map((d) => [d.slug, d]));
